import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowLeft as ChevronLeft, ArrowRight as ChevronRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { fetchArticle } from "../lib/api";
import { fmtLongDate, showsReadTime } from "../lib/dates";

export default function StudioLifeDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setArticle(null);
        setError(false);
        fetchArticle(slug).then(setArticle).catch(() => setError(true));
    }, [slug]);

    if (error) {
        return (
            <main
                className="pt-40 px-6 md:px-12 lg:px-24 pb-32"
                data-testid="studio-life-not-found"
            >
                <h1 className="serif text-4xl">Entry not found</h1>
                <Link to="/studio-life" className="asc-link mt-6 inline-block">
                    Back to Studio Life
                </Link>
            </main>
        );
    }
    if (!article) {
        return (
            <main
                className="pt-40 px-6 md:px-12 lg:px-24 pb-32"
                data-testid="studio-life-loading"
            >
                <p className="asc-overline">Loading…</p>
            </main>
        );
    }

    const showRead = showsReadTime(article.category);
    const meta = [
        article.category,
        fmtLongDate(article.publish_date),
        showRead ? article.read_time : "",
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <main data-testid="page-studio-life-detail" className="pt-28 md:pt-32">
            <Seo
                path={`/studio-life/${article.slug}`}
                title={article.seo_title || article.title}
                description={article.seo_description || article.deck}
                image={article.og_image || article.featured_image}
                type="article"
            />
            <section className="px-6 md:px-12 lg:px-24 pb-12">
                <Link
                    to="/studio-life"
                    data-testid="back-to-studio-life"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]"
                >
                    <ArrowLeft size={14} /> All Studio Life
                </Link>
            </section>

            {/* HEADER — full container width */}
            <section className="px-6 md:px-12 lg:px-24 pb-16">
                <div className="max-w-6xl">
                    <Reveal>
                        <p className="asc-overline">{meta}</p>
                    </Reveal>
                    <Reveal delay={100}>
                        <h1
                            data-testid="studio-life-title"
                            className="serif text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight font-light"
                        >
                            {article.title}
                        </h1>
                    </Reveal>
                    {article.deck && (
                        <Reveal delay={180}>
                            <p className="mt-8 text-xl lg:text-2xl text-[#5C5855] leading-relaxed max-w-4xl">
                                {article.deck}
                            </p>
                        </Reveal>
                    )}
                    {article.author && (
                        <Reveal delay={220}>
                            <p className="mt-6 asc-overline text-[#8C7A6B]">By {article.author}</p>
                        </Reveal>
                    )}
                </div>
            </section>

            {article.featured_image && (
                <section className="px-6 md:px-12 lg:px-24 pb-20">
                    <div className="asc-img-zoom aspect-[16/9]">
                        <img
                            src={article.featured_image}
                            alt={article.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </section>
            )}

            {/* BODY — expanded to standard content container, generous prose width */}
            <section className="px-6 md:px-12 lg:px-24 pb-20">
                <div
                    data-testid="studio-life-body"
                    className="max-w-4xl text-lg lg:text-xl text-[#1A1A1A] leading-[1.75] font-light whitespace-pre-line"
                >
                    {article.body}
                </div>
            </section>

            {/* GALLERY — horizontal slider */}
            {article.gallery && article.gallery.length > 0 && (
                <ArticleGallery images={article.gallery} title={article.title} />
            )}
        </main>
    );
}

function ArticleGallery({ images, title }) {
    const trackRef = useRef(null);
    const [index, setIndex] = useState(0);

    // Keyboard navigation
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images.length]);

    const scrollTo = (i) => {
        const clamped = Math.max(0, Math.min(images.length - 1, i));
        setIndex(clamped);
        const el = trackRef.current;
        if (!el) return;
        const slide = el.children[clamped];
        if (slide) {
            el.scrollTo({ left: slide.offsetLeft - el.offsetLeft, behavior: "smooth" });
        }
    };
    const next = () => scrollTo(index + 1);
    const prev = () => scrollTo(index - 1);

    const onScroll = () => {
        const el = trackRef.current;
        if (!el) return;
        const width = el.clientWidth;
        const i = Math.round(el.scrollLeft / width);
        if (i !== index) setIndex(i);
    };

    return (
        <section
            data-testid="studio-life-gallery"
            className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32"
        >
            <div className="relative">
                <div
                    ref={trackRef}
                    onScroll={onScroll}
                    className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4"
                    style={{ scrollbarWidth: "thin" }}
                >
                    {images.map((img, i) => (
                        <div
                            key={i}
                            data-testid={`gallery-slide-${i}`}
                            className="snap-start shrink-0 w-full aspect-[16/9] bg-[#F2EFEB] overflow-hidden"
                        >
                            <img
                                src={img}
                                alt={`${title} — image ${i + 1}`}
                                className="w-full h-full object-cover"
                                loading={i === 0 ? "eager" : "lazy"}
                            />
                        </div>
                    ))}
                </div>

                {images.length > 1 && (
                    <>
                        <button
                            onClick={prev}
                            disabled={index === 0}
                            data-testid="gallery-prev"
                            aria-label="Previous image"
                            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-[#F9F8F6]/90 border border-[#E5E1DB] text-[#1A1A1A] hover:bg-[#F9F8F6] disabled:opacity-30 disabled:pointer-events-none transition-opacity"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={next}
                            disabled={index === images.length - 1}
                            data-testid="gallery-next"
                            aria-label="Next image"
                            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-[#F9F8F6]/90 border border-[#E5E1DB] text-[#1A1A1A] hover:bg-[#F9F8F6] disabled:opacity-30 disabled:pointer-events-none transition-opacity"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </>
                )}
            </div>

            {images.length > 1 && (
                <div className="mt-6 flex items-center justify-between gap-4">
                    <div className="flex gap-2 flex-wrap" data-testid="gallery-thumbs">
                        {images.map((img, i) => (
                            <button
                                key={i}
                                onClick={() => scrollTo(i)}
                                aria-label={`Go to image ${i + 1}`}
                                data-testid={`gallery-thumb-${i}`}
                                className={`w-16 h-12 border overflow-hidden transition-opacity ${
                                    i === index
                                        ? "border-[#1A1A1A] opacity-100"
                                        : "border-[#E5E1DB] opacity-60 hover:opacity-100"
                                }`}
                            >
                                <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#8C7A6B]">
                        {index + 1} / {images.length}
                    </p>
                </div>
            )}
        </section>
    );
}
